// Music discovery page with filters and playback

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Music2Icon, Volume2Icon } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import SEO from '../../seo/SEO';
import { Poppy } from '../../components/avatars';
import type { PlaybackPoppyState } from '../../components/avatars/Poppy';
import {
  useMedia,
  useRouteQueryState,
  useSystemDeviceDetection,
  useSystemWindowSize,
} from '../../hooks';
import { Button } from '../../components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../components/ui/empty';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Slider } from '../../components/ui/slider';
import { MUSIC_LIBRARY } from '@airhead-games/shared/configs';
import {
  findGameDiscoverySlug,
  findGameIdByDiscoverySlug,
  findGameName,
  shuffleArray,
} from '@airhead-games/shared/utilities';
import { analyticsService } from '../../services/analyticsService';
import {
  CollectionToolbar,
  FilterControls,
  SortControl,
} from '../../components/collectionToolbar';
import type { Artist } from '@airhead-games/shared/types/configs/media/musicLibrary';
import { cn } from '@/utilities/cn';
import {
  DEFAULT_MUSIC_SORT_DIRECTION,
  DEFAULT_MUSIC_SORT_FIELD,
  MUSIC_SORT_FIELDS,
  MUSIC_SORT_OPTIONS,
  type MusicSortField,
  type SongEntry,
  sortMusicSongs,
} from './musicSort';
import { SORT_DIRECTIONS, type SortDirection } from '../../utilities/sort';

type GameOption = {
  id: string;
  label: string;
};

const formatDuration = (seconds: number) => {
  const totalSeconds = Number.isFinite(seconds)
    ? Math.max(0, Math.round(seconds))
    : 0;
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getGameLabel = (gameId: string) => {
  if (gameId === 'none') {
    return 'Lobby';
  }
  return findGameName(gameId) || 'Unknown Game';
};

type MusicAssetFolder = 'albums' | 'artists' | 'songs';

const MUSIC_ASSET_BASE_URL = 'https://assets.airheadgames.com/music';

const getMusicAssetUrl = (
  folder: MusicAssetFolder,
  fileBase?: string,
  extension = 'png'
) => {
  if (!fileBase) return undefined;
  return `${MUSIC_ASSET_BASE_URL}/${folder}/${fileBase}.${extension}`;
};

const resolveGameParam = (value: string, validGameIds: Set<string>) => {
  if (value === 'none') {
    return 'none';
  }
  const bySlug = findGameIdByDiscoverySlug(value);
  if (bySlug) {
    return bySlug;
  }
  if (validGameIds.has(value)) {
    return value;
  }
  return undefined;
};

const resolveArtistParam = (
  value: string,
  artistLookup: Map<string, Artist>
) => {
  if (artistLookup.has(value)) {
    return value;
  }
  const normalizedValue = value.toLowerCase();
  for (const [artistId, artist] of artistLookup.entries()) {
    if (artist.name.toLowerCase() === normalizedValue) {
      return artistId;
    }
  }
  return undefined;
};

const resolveStyleParam = (value: string, styleSet: Set<string>) => {
  if (styleSet.has(value)) {
    return value;
  }
  const normalizedValue = value.toLowerCase();
  for (const style of styleSet.values()) {
    if (style.toLowerCase() === normalizedValue) {
      return style;
    }
  }
  return undefined;
};

const toggleFilterValue = (values: string[], value: string) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

const matchesFilters = (
  entry: SongEntry,
  gameIds: string[],
  artistIds: string[],
  styles: string[]
) => {
  const matchesGame = gameIds.length === 0 || gameIds.includes(entry.song.game);
  const matchesArtist =
    artistIds.length === 0 || artistIds.includes(entry.artist.id);
  const tags = entry.song.tags ?? [];
  const matchesStyle =
    styles.length === 0 || tags.some((tag) => styles.includes(tag));
  return matchesGame && matchesArtist && matchesStyle;
};

const resolveSongForFilters = (
  songId: string | null,
  songEntries: SongEntry[],
  gameIds: string[],
  artistIds: string[],
  styles: string[]
) => {
  if (!songId) return null;
  const entry = songEntries.find((item) => item.song.id === songId);
  if (!entry) return null;
  return matchesFilters(entry, gameIds, artistIds, styles) ? songId : null;
};

const getPlaybackFilterButtonClass = (selected: boolean) =>
  cn(
    'inline-flex h-8 items-center rounded-full border px-3 text-sm font-medium transition-all backdrop-blur-sm',
    selected
      ? 'border-lime-300/30 bg-lime-300/12 text-white hover:border-lime-200/45 hover:bg-lime-300/18'
      : 'border-white/10 bg-black/25 text-white/82 hover:border-white/25 hover:bg-white/10 hover:text-white'
  );

const Music: React.FC = () => {
  const navigate = useNavigate();
  const { musicAudioRef, resumeAudioContext, setIsMuted } = useMedia();
  const { width: rawViewportWidth, height: rawViewportHeight } =
    useSystemWindowSize(120);
  const { deviceType } = useSystemDeviceDetection();
  const wasPlayingRef = useRef(false);
  const songSyncRef = useRef<string | null>(null);
  const [currentSongId, setCurrentSongId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [searchParams] = useSearchParams();

  const songEntries = useMemo<SongEntry[]>(() => {
    return MUSIC_LIBRARY.artists.flatMap((artist) =>
      artist.albums.flatMap((album) =>
        album.songs.map((song) => ({ song, album, artist }))
      )
    );
  }, []);

  const shuffledOrder = useMemo(() => {
    const order = songEntries.map((_, index) => index);
    return shuffleArray(order);
  }, [songEntries]);

  const allGameIds = useMemo(() => {
    return new Set(songEntries.map((entry) => entry.song.game));
  }, [songEntries]);

  const allStyles = useMemo(() => {
    const styles = new Set<string>();
    songEntries.forEach((entry) => {
      entry.song.tags?.forEach((tag) => styles.add(tag));
    });
    return styles;
  }, [songEntries]);

  const artistLookup = useMemo(() => {
    return new Map(MUSIC_LIBRARY.artists.map((artist) => [artist.id, artist]));
  }, []);

  const { selected, values, buildSearch, updateRoute, updateEnums } =
    useRouteQueryState<
      { games: string[]; artists: string[]; styles: string[] },
      { sortField: MusicSortField; sortDirection: SortDirection }
    >({
      pathname: '/discover/music',
      searchParams,
      navigate,
      facets: {
        games: {
          key: 'game',
          parse: (raw) => resolveGameParam(raw, allGameIds),
          serialize: (gameId) =>
            gameId === 'none'
              ? 'none'
              : findGameDiscoverySlug(gameId) || gameId,
        },
        artists: {
          key: 'artist',
          parse: (raw) => resolveArtistParam(raw, artistLookup),
        },
        styles: {
          key: 'style',
          parse: (raw) => resolveStyleParam(raw, allStyles),
        },
      },
      enums: {
        sortField: {
          key: 'sort',
          values: MUSIC_SORT_FIELDS,
          default: DEFAULT_MUSIC_SORT_FIELD,
        },
        sortDirection: {
          key: 'dir',
          values: SORT_DIRECTIONS,
          default: DEFAULT_MUSIC_SORT_DIRECTION,
        },
      },
      singleKeys: ['song'],
    });

  const selectedGames = selected.games;
  const selectedArtists = selected.artists;
  const selectedGenres = selected.styles;
  const { sortField, sortDirection } = values;

  const { matchesAll, genreOptions, artistOptions, gameOptions } =
    useMemo(() => {
      const selectedGenresSet = new Set(selectedGenres);
      const selectedArtistsSet = new Set(selectedArtists);
      const selectedGamesSet = new Set(selectedGames);

      const genreSet = new Set<string>();
      const artistMap = new Map<string, Artist>();
      const gameIdSet = new Set<string>();
      const matchesAll: boolean[] = new Array(songEntries.length);

      for (let i = 0; i < songEntries.length; i += 1) {
        const entry = songEntries[i];
        const entryTags = entry.song.tags ?? [];
        const matchesArtist =
          selectedArtistsSet.size === 0 ||
          selectedArtistsSet.has(entry.artist.id);
        const matchesGame =
          selectedGamesSet.size === 0 || selectedGamesSet.has(entry.song.game);
        const matchesGenre =
          selectedGenresSet.size === 0 ||
          entryTags.some((tag) => selectedGenresSet.has(tag));

        if (matchesArtist && matchesGame) {
          entryTags.forEach((tag) => genreSet.add(tag));
        }
        if (matchesGame && matchesGenre) {
          if (!artistMap.has(entry.artist.id)) {
            artistMap.set(entry.artist.id, entry.artist);
          }
        }
        if (matchesArtist && matchesGenre) {
          gameIdSet.add(entry.song.game);
        }

        matchesAll[i] = matchesArtist && matchesGame && matchesGenre;
      }

      const genreOptions = Array.from(genreSet).sort((a, b) =>
        a.localeCompare(b)
      );
      const artistOptions = Array.from(artistMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const gameOptions = Array.from(gameIdSet)
        .map((gameId) => ({
          id: gameId,
          label: getGameLabel(gameId),
        }))
        .sort((a, b) => {
          if (a.id === 'none') return 1;
          if (b.id === 'none') return -1;
          return a.label.localeCompare(b.label);
        });

      return { matchesAll, genreOptions, artistOptions, gameOptions };
    }, [selectedArtists, selectedGames, selectedGenres, songEntries]);

  const filteredSongs = useMemo<SongEntry[]>(() => {
    if (sortField === 'shuffle') {
      const ordered: SongEntry[] = [];
      for (const index of shuffledOrder) {
        if (matchesAll[index]) {
          ordered.push(songEntries[index]);
        }
      }
      return ordered;
    }
    const filtered: SongEntry[] = [];
    for (let i = 0; i < songEntries.length; i += 1) {
      if (matchesAll[i]) {
        filtered.push(songEntries[i]);
      }
    }
    return sortMusicSongs(filtered, sortField, sortDirection);
  }, [matchesAll, shuffledOrder, songEntries, sortField, sortDirection]);

  const totalDuration = useMemo(
    () =>
      filteredSongs.reduce(
        (sum, entry) =>
          sum +
          (Number.isFinite(entry.song.duration) ? entry.song.duration : 0),
        0
      ),
    [filteredSongs]
  );

  const orderedGenres = useMemo(() => {
    const selectedSet = new Set(selectedGenres);
    return [
      ...selectedGenres,
      ...genreOptions.filter((genre) => !selectedSet.has(genre)),
    ];
  }, [selectedGenres, genreOptions]);

  const orderedArtists = useMemo(() => {
    const selectedSet = new Set(selectedArtists);
    return [
      ...selectedArtists
        .map(
          (artistId) =>
            artistOptions.find((artist) => artist.id === artistId) ||
            artistLookup.get(artistId)
        )
        .filter((artist): artist is Artist => Boolean(artist)),
      ...artistOptions.filter((artist) => !selectedSet.has(artist.id)),
    ];
  }, [artistOptions, artistLookup, selectedArtists]);

  const orderedGames = useMemo(() => {
    const selectedSet = new Set(selectedGames);
    return [
      ...selectedGames
        .map(
          (gameId) =>
            gameOptions.find((option) => option.id === gameId) || {
              id: gameId,
              label: getGameLabel(gameId),
            }
        )
        .filter((option): option is GameOption => Boolean(option)),
      ...gameOptions.filter((option) => !selectedSet.has(option.id)),
    ];
  }, [gameOptions, selectedGames]);

  const currentEntry = useMemo(() => {
    if (!currentSongId) return null;
    return songEntries.find((entry) => entry.song.id === currentSongId) || null;
  }, [songEntries, currentSongId]);

  useEffect(() => {
    if (!currentSongId && shuffledOrder.length > 0) {
      const firstEntry = songEntries[shuffledOrder[0]];
      if (firstEntry) {
        setCurrentSongId(firstEntry.song.id);
      }
    }
  }, [currentSongId, shuffledOrder, songEntries]);

  const playEntry = useCallback(
    async (entry: SongEntry) => {
      const audio = musicAudioRef.current;
      if (!audio) return;
      const src = getMusicAssetUrl('songs', entry.song.pathPublic, 'mp3');
      if (!src) return;

      if (audio.src !== src) {
        audio.src = src;
        audio.currentTime = 0;
      }

      audio.loop = false;
      audio.preload = 'metadata';

      try {
        setIsMuted(false);
        await resumeAudioContext();
        await audio.play();
      } catch {
        // Ignore autoplay or playback errors.
      }
    },
    [musicAudioRef, resumeAudioContext, setIsMuted]
  );

  const updateFilters = useCallback(
    (
      next: {
        games?: string[];
        artists?: string[];
        styles?: string[];
        songId?: string | null;
      },
      options?: { replace?: boolean }
    ) => {
      const nextGames = next.games ?? selectedGames;
      const nextArtists = next.artists ?? selectedArtists;
      const nextStyles = next.styles ?? selectedGenres;
      const requestedSongId =
        typeof next.songId !== 'undefined' ? next.songId : currentSongId;
      const nextSongId = resolveSongForFilters(
        requestedSongId,
        songEntries,
        nextGames,
        nextArtists,
        nextStyles
      );
      updateRoute(
        {
          games: nextGames,
          artists: nextArtists,
          styles: nextStyles,
        },
        {
          singles: { song: nextSongId },
          replace: options?.replace,
        }
      );
    },
    [
      currentSongId,
      selectedArtists,
      selectedGenres,
      selectedGames,
      songEntries,
      updateRoute,
    ]
  );

  const clearAllFilters = useCallback(() => {
    updateFilters({
      games: [],
      artists: [],
      styles: [],
    });
  }, [updateFilters]);

  const setSortField = useCallback(
    (next: MusicSortField) => {
      updateEnums({ sortField: next });
    },
    [updateEnums]
  );

  const setSortDirection = useCallback(
    (next: SortDirection) => {
      updateEnums({ sortDirection: next });
    },
    [updateEnums]
  );

  const handleSeekStart = useCallback(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;
    wasPlayingRef.current = !audio.paused || audio.ended;
  }, [musicAudioRef]);

  const handleSeekEnd = useCallback(() => {
    const audio = musicAudioRef.current;
    if (!audio || !wasPlayingRef.current) return;
    wasPlayingRef.current = false;
    void resumeAudioContext();
    void audio.play();
  }, [musicAudioRef, resumeAudioContext]);

  const handleArtistFilter = useCallback(() => {
    if (!currentEntry) return;
    const isRemoving = selectedArtists.includes(currentEntry.artist.id);
    analyticsService.trackButtonClick('Music Filter Artist', {
      filter_value: currentEntry.artist.name,
      filter_action: isRemoving ? 'remove' : 'add',
      link_location: 'music_now_playing',
    });
    updateFilters({
      artists: toggleFilterValue(selectedArtists, currentEntry.artist.id),
    });
  }, [currentEntry, selectedArtists, updateFilters]);

  const trackFilterChange = useCallback(
    (
      filterType: 'genre' | 'artist' | 'game',
      value: string,
      action: 'add' | 'remove'
    ) => {
      analyticsService.trackButtonClick(`Music Filter ${filterType}`, {
        filter_value: value,
        filter_action: action,
        link_location: 'music_filter_panel',
      });
    },
    []
  );

  const selectSong = useCallback(
    (
      entry: SongEntry,
      options?: { autoplay?: boolean; updateUrl?: boolean }
    ) => {
      setCurrentSongId(entry.song.id);
      if (options?.updateUrl) {
        updateFilters({ songId: entry.song.id });
      }
      if (options?.autoplay) {
        analyticsService.trackButtonClick('Song Play', {
          song_title: entry.song.title,
          artist_name: entry.artist.name,
          album_name: entry.album.name,
          game_id: entry.song.game,
          link_location: 'music_tracklist',
        });
        void playEntry(entry);
      }
    },
    [playEntry, updateFilters]
  );

  const currentIndex = useMemo(() => {
    return filteredSongs.findIndex((entry) => entry.song.id === currentSongId);
  }, [filteredSongs, currentSongId]);

  const handleNext = useCallback(() => {
    if (filteredSongs.length === 0) return;
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + 1) % filteredSongs.length;
    selectSong(filteredSongs[nextIndex], { autoplay: true });
  }, [filteredSongs, currentIndex, selectSong]);

  const handleTogglePlayback = useCallback(async () => {
    const audio = musicAudioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      analyticsService.trackButtonClick('Music Pause', {
        song_title: currentEntry?.song.title,
        link_location: 'music_player',
      });
    } else if (currentEntry) {
      analyticsService.trackButtonClick('Music Play', {
        song_title: currentEntry.song.title,
        artist_name: currentEntry.artist.name,
        link_location: 'music_player',
      });
      await playEntry(currentEntry);
    }
  }, [musicAudioRef, isPlaying, currentEntry, playEntry]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;

    const handleAudioEnded = () => handleNext();

    audio.addEventListener('ended', handleAudioEnded);

    return () => {
      audio.removeEventListener('ended', handleAudioEnded);
    };
  }, [musicAudioRef, handleNext]);

  const validSongParam = useMemo(() => {
    const songParam = searchParams.get('song');
    if (!songParam) return null;
    return resolveSongForFilters(
      songParam,
      songEntries,
      selectedGames,
      selectedArtists,
      selectedGenres
    );
  }, [
    searchParams,
    selectedArtists,
    selectedGenres,
    selectedGames,
    songEntries,
  ]);

  const resolvedSongId = useMemo(() => {
    return resolveSongForFilters(
      currentSongId,
      songEntries,
      selectedGames,
      selectedArtists,
      selectedGenres
    );
  }, [
    currentSongId,
    selectedArtists,
    selectedGenres,
    selectedGames,
    songEntries,
  ]);

  useEffect(() => {
    if (!validSongParam) {
      songSyncRef.current = null;
      return;
    }
    if (validSongParam === currentSongId) {
      songSyncRef.current = null;
      return;
    }
    const entry = songEntries.find(
      (songEntry) => songEntry.song.id === validSongParam
    );
    if (!entry) return;
    const audio = musicAudioRef.current;
    const shouldAutoplay = Boolean(audio && !audio.paused);
    songSyncRef.current = validSongParam;
    selectSong(entry, shouldAutoplay ? { autoplay: true } : undefined);
  }, [validSongParam, currentSongId, musicAudioRef, songEntries, selectSong]);

  const normalizedSearch = useMemo(
    () =>
      buildSearch(
        {
          games: selectedGames,
          artists: selectedArtists,
          styles: selectedGenres,
        },
        { singles: { song: resolvedSongId } }
      ),
    [
      buildSearch,
      selectedArtists,
      selectedGenres,
      selectedGames,
      resolvedSongId,
    ]
  );

  useEffect(() => {
    if (songSyncRef.current) {
      return;
    }
    const currentSearch = searchParams.toString();
    if (normalizedSearch.replace(/^\?/, '') !== currentSearch) {
      navigate(`/discover/music${normalizedSearch}`, { replace: true });
    }
  }, [navigate, normalizedSearch, searchParams]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleDurationChange = () =>
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleDurationChange);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('seeked', handleTimeUpdate);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleDurationChange);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('seeked', handleTimeUpdate);
    };
  }, [musicAudioRef]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    setIsPlaying(!audio.paused);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [musicAudioRef]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio || !currentEntry) return;
    const src = getMusicAssetUrl('songs', currentEntry.song.pathPublic, 'mp3');
    if (!src) return;
    if (audio.src !== src) {
      audio.src = src;
      audio.preload = 'metadata';
      audio.load();
    }
  }, [currentEntry, musicAudioRef]);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
  }, [currentEntry?.song.id]);

  useEffect(() => {
    if (filteredSongs.length === 0) {
      setCurrentSongId(null);
      const audio = musicAudioRef.current;
      if (audio) {
        audio.pause();
      }
      return;
    }

    if (
      !currentSongId ||
      !filteredSongs.some((entry) => entry.song.id === currentSongId)
    ) {
      const nextEntry = filteredSongs[0];
      setCurrentSongId(nextEntry.song.id);
      const audio = musicAudioRef.current;
      if (audio && !audio.paused) {
        void playEntry(nextEntry);
      }
    }
  }, [filteredSongs, currentSongId, musicAudioRef, playEntry]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    return () => {
      if (!audio) return;
      audio.pause();
      audio.src = '';
    };
  }, [musicAudioRef]);

  const nowPlayingTitle = currentEntry?.song.title || 'Select a track';
  const nowPlayingArtist = currentEntry?.artist.name || 'Unknown Artist';
  const nowPlayingAlbum = currentEntry?.album.name || 'Unknown Album';
  const nowPlayingYear = currentEntry?.album.year;
  const nowPlayingTags = currentEntry?.song.tags ?? [];
  const nowPlayingGameId = currentEntry?.song.game;
  const nowPlayingGameLabel = nowPlayingGameId
    ? getGameLabel(nowPlayingGameId)
    : undefined;
  const flooredCurrentTime = Math.floor(currentTime);
  const flooredDuration = Math.floor(duration);
  const remainingTime = Math.max(0, flooredDuration - flooredCurrentTime);
  const seekMax = duration || 0;
  const seekValue = Math.min(currentTime, seekMax);

  const albumArtUrl = currentEntry
    ? getMusicAssetUrl('albums', currentEntry.album.coverImagePublic)
    : undefined;
  const playbackState: PlaybackPoppyState = {
    isPlaying,
    onToggle: handleTogglePlayback,
  };
  const activeFilterItems = useMemo(
    () => [
      ...selectedGenres.map((genre) => ({
        id: `style:${genre}`,
        label: genre,
        onRemove: () => {
          trackFilterChange('genre', genre, 'remove');
          updateFilters({
            styles: selectedGenres.filter((item) => item !== genre),
          });
        },
      })),
      ...selectedArtists.map((artistId) => {
        const artist = artistLookup.get(artistId);
        const label = artist?.name || artistId;
        return {
          id: `artist:${artistId}`,
          label,
          onRemove: () => {
            trackFilterChange('artist', label, 'remove');
            updateFilters({
              artists: selectedArtists.filter((item) => item !== artistId),
            });
          },
        };
      }),
      ...selectedGames.map((gameId) => {
        const gameLabel = getGameLabel(gameId);
        return {
          id: `game:${gameId}`,
          label: gameLabel,
          onRemove: () => {
            trackFilterChange('game', gameLabel, 'remove');
            updateFilters({
              games: selectedGames.filter((item) => item !== gameId),
            });
          },
        };
      }),
    ],
    [
      artistLookup,
      selectedArtists,
      selectedGames,
      selectedGenres,
      trackFilterChange,
      updateFilters,
    ]
  );
  const hasActiveFilters = activeFilterItems.length > 0;
  const viewportWidth = rawViewportWidth || 1440;
  const viewportHeight = rawViewportHeight || 900;
  const isCompactLandscapeTablet =
    (deviceType === 'tablet' &&
      viewportWidth > viewportHeight &&
      viewportHeight <= 820) ||
    viewportHeight <= 820;
  const basePoppyScale =
    viewportWidth <= 480
      ? 0.429
      : viewportWidth <= 720
        ? 0.571
        : viewportWidth <= 900
          ? 0.786
          : viewportWidth <= 1100
            ? 0.821
            : viewportWidth <= 1200
              ? 0.857
              : 1;
  const poppyScale = isCompactLandscapeTablet
    ? Math.min(basePoppyScale, 0.72)
    : basePoppyScale;
  const poppyWrapperSize =
    viewportWidth <= 480
      ? 60
      : viewportWidth <= 720
        ? 80
        : viewportWidth <= 900
          ? 110
          : Math.round(280 * poppyScale);
  const progressWidth = viewportWidth <= 900 ? '100%' : `${poppyWrapperSize}px`;
  const trackCountLabel = `${filteredSongs.length} track${
    filteredSongs.length === 1 ? '' : 's'
  }`;

  return (
    <div className="h-svh overflow-hidden bg-background text-foreground">
      <SEO />
      <main className="mx-auto flex h-full w-full max-w-screen-2xl min-w-0 flex-col gap-4 overflow-hidden px-4 pt-20 pb-4 sm:gap-5 sm:px-6 sm:pt-24 md:px-8 md:pt-28 lg:gap-6 lg:px-10 lg:pt-32 xl:px-12 2xl:px-16">
        {/* The page needs a stable heading of its own: the visible title below
         * is the now-playing track, which changes as you browse. */}
        <h1 className="sr-only">Our Music</h1>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-col gap-8 bg-background">
            <nav
              aria-label="Breadcrumb"
              className="text-sm text-muted-foreground"
            >
              <ol className="flex flex-wrap items-center gap-2">
                <li className="flex items-center gap-2">
                  <Link to="/discover" className="hover:text-primary">
                    Discover
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <span aria-hidden="true">/</span>
                  <span className="text-foreground">Music</span>
                </li>
              </ol>
            </nav>

            <div
              className={cn(
                'flex flex-col bg-background',
                isCompactLandscapeTablet ? 'gap-3' : 'gap-4 sm:gap-5'
              )}
            >
              <section className="dark relative shrink-0 overflow-hidden rounded-lg bg-background text-foreground shadow-2xl ring-1 ring-border">
                {albumArtUrl && (
                  <div
                    className="absolute inset-0 scale-150 translate-y-[15%] bg-cover bg-[center_70%] opacity-40 blur-[24px]"
                    style={{ backgroundImage: `url(${albumArtUrl})` }}
                    aria-hidden="true"
                  />
                )}
                <div
                  className="absolute inset-0 bg-linear-to-t from-black/70 via-black/40 to-black/20"
                  aria-hidden="true"
                />
                <div
                  className={cn(
                    'relative grid items-center [grid-template-columns:minmax(0,1fr)_auto] max-[900px]:grid-cols-[1fr_auto] max-[900px]:grid-rows-[auto_auto] max-[900px]:gap-x-2 max-[900px]:gap-y-4 max-[720px]:p-4',
                    isCompactLandscapeTablet ? 'gap-4 p-4' : 'gap-6 p-6'
                  )}
                >
                  <div
                    className={cn(
                      'flex min-w-0 flex-col max-[900px]:col-start-1 max-[900px]:row-start-1',
                      isCompactLandscapeTablet ? 'gap-3' : 'gap-5'
                    )}
                  >
                    <div
                      className={cn(
                        'flex min-w-0 items-center max-[900px]:gap-2',
                        isCompactLandscapeTablet ? 'gap-4' : 'gap-6'
                      )}
                    >
                      <div
                        className={cn(
                          'flex aspect-square shrink-0 items-center justify-center overflow-hidden border border-white/10 bg-black shadow-lg max-[720px]:w-20 max-[720px]:rounded-lg max-[480px]:w-16',
                          isCompactLandscapeTablet
                            ? 'w-[176px] rounded-xl'
                            : 'w-[clamp(230px,22vw,300px)] rounded-2xl'
                        )}
                      >
                        {albumArtUrl ? (
                          <img
                            src={albumArtUrl}
                            alt={`${nowPlayingAlbum} cover`}
                            className="size-full object-cover"
                          />
                        ) : (
                          <Music2Icon
                            aria-hidden="true"
                            className="size-8 text-white/60 sm:size-10"
                          />
                        )}
                      </div>
                      <div
                        className={cn(
                          'flex min-w-0 flex-1 flex-col max-[720px]:gap-0.5',
                          isCompactLandscapeTablet ? 'gap-1' : 'gap-1.5'
                        )}
                      >
                        <h2
                          className={cn(
                            'font-heading leading-tight font-semibold tracking-tight text-white max-[720px]:text-[1.1rem] max-[480px]:text-base',
                            isCompactLandscapeTablet
                              ? 'text-[clamp(1.45rem,1.9vw,2rem)]'
                              : 'text-[clamp(1.8rem,2.4vw,2.6rem)]'
                          )}
                        >
                          {nowPlayingTitle}
                        </h2>
                        <button
                          type="button"
                          className={cn(
                            'w-fit text-left leading-tight font-medium text-white transition-colors hover:text-primary disabled:cursor-default disabled:text-white max-[720px]:text-[0.95rem] max-[480px]:text-[0.9rem]',
                            isCompactLandscapeTablet
                              ? 'text-[clamp(1rem,1.3vw,1.35rem)]'
                              : 'text-[clamp(1.2rem,1.6vw,1.8rem)]'
                          )}
                          onClick={handleArtistFilter}
                          disabled={!currentEntry}
                        >
                          {nowPlayingArtist}
                        </button>
                        <p
                          className={cn(
                            'leading-tight text-white/70 max-[720px]:text-[0.85rem] max-[480px]:text-[0.8rem]',
                            isCompactLandscapeTablet
                              ? 'text-[clamp(0.95rem,1.1vw,1.1rem)]'
                              : 'text-[clamp(1.05rem,1.3vw,1.4rem)]'
                          )}
                        >
                          {nowPlayingAlbum}
                          {nowPlayingYear ? ` • ${nowPlayingYear}` : ''}
                        </p>
                        <div
                          className={cn(
                            'hidden flex-wrap max-[720px]:hidden sm:flex',
                            isCompactLandscapeTablet
                              ? 'mt-1.5 gap-1.5'
                              : 'mt-2 gap-2'
                          )}
                        >
                          {nowPlayingTags.map((tag, index) => {
                            const isSelected = selectedGenres.includes(tag);
                            return (
                              <button
                                key={`${tag}-${index}`}
                                type="button"
                                aria-pressed={isSelected}
                                className={getPlaybackFilterButtonClass(
                                  isSelected
                                )}
                                onClick={() => {
                                  trackFilterChange(
                                    'genre',
                                    tag,
                                    isSelected ? 'remove' : 'add'
                                  );
                                  updateFilters({
                                    styles: toggleFilterValue(
                                      selectedGenres,
                                      tag
                                    ),
                                  });
                                }}
                              >
                                {tag}
                              </button>
                            );
                          })}
                          {nowPlayingGameLabel && nowPlayingGameId ? (
                            <button
                              type="button"
                              aria-pressed={selectedGames.includes(
                                nowPlayingGameId
                              )}
                              className={getPlaybackFilterButtonClass(
                                selectedGames.includes(nowPlayingGameId)
                              )}
                              onClick={() => {
                                const isSelected =
                                  selectedGames.includes(nowPlayingGameId);
                                trackFilterChange(
                                  'game',
                                  nowPlayingGameLabel,
                                  isSelected ? 'remove' : 'add'
                                );
                                updateFilters({
                                  games: toggleFilterValue(
                                    selectedGames,
                                    nowPlayingGameId
                                  ),
                                });
                              }}
                            >
                              {nowPlayingGameLabel}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      'flex flex-col items-end justify-center justify-self-end max-[900px]:contents',
                      isCompactLandscapeTablet ? 'gap-2.5' : 'gap-3.5'
                    )}
                  >
                    <div className="flex items-center justify-center self-end max-[900px]:col-start-2 max-[900px]:row-start-1 max-[900px]:self-center">
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: poppyWrapperSize,
                          height: poppyWrapperSize,
                        }}
                      >
                        <div
                          className="flex items-center justify-center"
                          style={{
                            transform: `scale(${poppyScale})`,
                            transformOrigin: 'center center',
                          }}
                        >
                          <Poppy
                            size={280}
                            disableMenu
                            playbackState={playbackState}
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      className={cn(
                        'flex flex-col items-end max-[900px]:col-span-2 max-[900px]:row-start-2 max-[900px]:w-full max-[900px]:items-center',
                        isCompactLandscapeTablet ? 'gap-1.5' : 'gap-2'
                      )}
                      style={{ width: progressWidth }}
                    >
                      <Slider
                        aria-label="Seek playback position"
                        min={0}
                        max={seekMax || 1}
                        step={0.1}
                        value={[seekValue]}
                        disabled={!duration}
                        onPointerDownCapture={handleSeekStart}
                        onPointerUpCapture={handleSeekEnd}
                        onPointerCancel={handleSeekEnd}
                        onValueChange={(values) => {
                          const audio = musicAudioRef.current;
                          if (!audio) return;
                          const nextTime = values[0] ?? 0;
                          audio.currentTime = nextTime;
                          setCurrentTime(nextTime);
                        }}
                        onValueCommit={() => handleSeekEnd()}
                        className="w-full py-0.5 data-[disabled]:cursor-not-allowed [&_[data-slot=slider-thumb]]:size-6 [&_[data-slot=slider-thumb]]:border-2 [&_[data-slot=slider-thumb]]:border-black [&_[data-slot=slider-thumb]]:bg-primary [&_[data-slot=slider-track]]:h-3 [&_[data-slot=slider-track]]:bg-white/18"
                      />
                      <div className="flex w-full items-center justify-between text-base font-medium tabular-nums text-white/70 max-[720px]:text-sm">
                        <span>{formatDuration(flooredCurrentTime)}</span>
                        <span>-{formatDuration(remainingTime)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div
                className={cn(
                  isCompactLandscapeTablet ? 'pt-0.5' : 'pt-1 sm:pt-2'
                )}
              >
                <CollectionToolbar
                  filters={
                    <FilterControls
                      groups={[
                        {
                          id: 'styles',
                          label: 'Styles',
                          options: orderedGenres.map((genre) => ({
                            value: genre,
                            label: genre,
                          })),
                          selectedValues: selectedGenres,
                          onToggle: (genre) => {
                            const isSelected = selectedGenres.includes(genre);
                            trackFilterChange(
                              'genre',
                              genre,
                              isSelected ? 'remove' : 'add'
                            );
                            updateFilters({
                              styles: toggleFilterValue(selectedGenres, genre),
                            });
                          },
                          searchPlaceholder: 'Search styles',
                        },
                        {
                          id: 'artists',
                          label: 'Artists',
                          options: orderedArtists.map((artist) => ({
                            value: artist.id,
                            label: artist.name,
                          })),
                          selectedValues: selectedArtists,
                          onToggle: (artistId) => {
                            const artist = artistLookup.get(artistId);
                            const label = artist?.name || artistId;
                            const isSelected =
                              selectedArtists.includes(artistId);
                            trackFilterChange(
                              'artist',
                              label,
                              isSelected ? 'remove' : 'add'
                            );
                            updateFilters({
                              artists: toggleFilterValue(
                                selectedArtists,
                                artistId
                              ),
                            });
                          },
                          searchPlaceholder: 'Search artists',
                        },
                        {
                          id: 'games',
                          label: 'Games',
                          options: orderedGames.map((game) => ({
                            value: game.id,
                            label: game.label,
                          })),
                          selectedValues: selectedGames,
                          onToggle: (gameId) => {
                            const gameLabel = getGameLabel(gameId);
                            const isSelected = selectedGames.includes(gameId);
                            trackFilterChange(
                              'game',
                              gameLabel,
                              isSelected ? 'remove' : 'add'
                            );
                            updateFilters({
                              games: toggleFilterValue(selectedGames, gameId),
                            });
                          },
                          searchPlaceholder: 'Search games',
                        },
                      ]}
                      activeItems={activeFilterItems}
                      onClearAll={clearAllFilters}
                    />
                  }
                  sort={
                    <SortControl
                      options={MUSIC_SORT_OPTIONS}
                      field={sortField}
                      direction={sortDirection}
                      onFieldChange={setSortField}
                      onDirectionChange={setSortDirection}
                    />
                  }
                />
              </div>

              <section className="min-w-0">
                <div className="flex items-center justify-between gap-4 border-b border-border px-3 pb-2">
                  <div className="flex min-w-0 items-baseline gap-3 max-[480px]:gap-2">
                    <h2 className="font-heading text-xl leading-tight font-semibold tracking-tight md:text-2xl">
                      Songs
                    </h2>
                    <span className="text-sm leading-6 text-muted-foreground max-[480px]:text-xs">
                      {trackCountLabel}
                    </span>
                  </div>
                  <span className="min-w-[56px] shrink-0 text-right text-sm leading-6 tabular-nums text-muted-foreground">
                    {formatDuration(totalDuration)}
                  </span>
                </div>
              </section>
            </div>
          </div>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            {filteredSongs.length === 0 ? (
              <Empty className="flex-1 border bg-muted/10 py-16">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Music2Icon aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>No songs match these filters</EmptyTitle>
                  <EmptyDescription>
                    Try removing a style, artist, or game filter to broaden the
                    mix.
                  </EmptyDescription>
                </EmptyHeader>
                {hasActiveFilters ? (
                  <EmptyContent>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                    >
                      Clear all filters
                    </Button>
                  </EmptyContent>
                ) : null}
              </Empty>
            ) : (
              <ScrollArea className="min-h-0 flex-1">
                <div className="flex flex-col divide-y divide-border">
                  {filteredSongs.map((entry) => {
                    const isActive = entry.song.id === currentSongId;
                    return (
                      <button
                        key={entry.song.id}
                        type="button"
                        aria-pressed={isActive}
                        className={cn(
                          'relative flex w-full items-center justify-between gap-4 py-2 pr-3 text-left transition-colors hover:bg-muted/40',
                          isActive
                            ? 'bg-muted/30 pl-12 hover:bg-muted/40'
                            : 'pl-3'
                        )}
                        onClick={() =>
                          selectSong(entry, {
                            autoplay: true,
                            updateUrl: true,
                          })
                        }
                      >
                        <div className="flex min-w-0 flex-1 items-start gap-3">
                          {isActive ? (
                            <Volume2Icon
                              aria-hidden="true"
                              className="absolute top-1/2 left-3.5 -translate-y-1/2 text-primary"
                            />
                          ) : null}
                          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                            <span className="font-medium text-foreground">
                              {entry.song.title}
                            </span>
                            <span
                              aria-hidden="true"
                              className="text-muted-foreground/60"
                            >
                              ·
                            </span>
                            <span className="text-muted-foreground">
                              {entry.artist.name}
                            </span>
                            <span
                              aria-hidden="true"
                              className="text-muted-foreground/60"
                            >
                              ·
                            </span>
                            <span className="text-muted-foreground">
                              {entry.album.name}
                            </span>
                          </span>
                        </div>
                        <span className="min-w-[56px] shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                          {formatDuration(entry.song.duration)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Music;
